import { mapExchange } from '@urql/core'
import { getIsOnline } from '../../helpers/networkStatus'
import { GRACE_MS } from '../../helpers/submitQueueable'

/**
 * Sync state store: last successful round-trip ("last synced" on Profile) and
 * a failure channel for background mutation rejections — graphcache reverts
 * their optimistic state silently (CRE-80). The tracker sits just above
 * fetchExchange so it only ever sees genuine network results.
 */

const LAST_SYNC_KEY = 'crew:lastSyncAt'

type Listener = () => void
const listeners = new Set<Listener>()

let lastSyncAt: string | null =
  typeof localStorage !== 'undefined'
    ? localStorage.getItem(LAST_SYNC_KEY)
    : null

let failureCount = 0
let lastFailureMessage: string | null = null
let inFlightMutations = 0
let replayWindowUntil = 0

const notify = () => {
  for (const listener of listeners) {
    try {
      listener()
    } catch (err) {
      // a misbehaving subscriber must not stop the others from being notified
      console.error('[OfflineSync] listener failed', err)
    }
  }
}

export const offlineSyncStore = {
  getLastSyncAt: () => lastSyncAt,
  getFailureCount: () => failureCount,
  getLastFailureMessage: () => lastFailureMessage,
  getInFlightMutations: () => inFlightMutations,
  subscribe: (listener: Listener) => {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
  markSynced: () => {
    lastSyncAt = new Date().toISOString()
    try {
      localStorage.setItem(LAST_SYNC_KEY, lastSyncAt)
    } catch {
      /* storage unavailable — keep the in-memory value */
    }
    notify()
  },
  recordFailure: (message: string) => {
    failureCount += 1
    lastFailureMessage = message
    notify()
  },
  /** Reset after a successful manual sync — the indicator shows current state. */
  clearFailures: () => {
    if (failureCount === 0) return
    failureCount = 0
    lastFailureMessage = null
    notify()
  },
  setInFlightMutations: (count: number) => {
    inFlightMutations = count
  },
  /** Marks boot-time replay of a persisted queue (hydration uses new op keys). */
  markReplayWindow: (ms = 30_000) => {
    replayWindowUntil = Date.now() + ms
  },
  isInReplayWindow: () => Date.now() < replayWindowUntil,
}

// Past the submit grace window the modal is gone — errors become background.
const BACKGROUND_MS = GRACE_MS + 1000

const tracked = new Map<number, { at: number; offline: boolean }>()

/**
 * Records the last successful network result and surfaces background mutation
 * failures. Place just above fetchExchange.
 */
export const syncTrackerExchange = mapExchange({
  onOperation(operation) {
    if (operation.kind === 'teardown') {
      tracked.delete(operation.key)
      return
    }
    // First dispatch wins — retries/replays reuse the same key.
    const isUntrackedMutation =
      operation.kind === 'mutation' && !tracked.has(operation.key)
    if (isUntrackedMutation) {
      tracked.set(operation.key, { at: Date.now(), offline: !getIsOnline() })
    }
  },
  onResult(result) {
    const operation = result.operation
    if (!result.error) {
      offlineSyncStore.markSynced()
      if (operation.kind === 'mutation') tracked.delete(operation.key)
      return result
    }

    // Network errors stay tracked — the same key replays later.
    if (operation.kind === 'mutation' && !result.error.networkError) {
      const entry = tracked.get(operation.key)
      const isBackground =
        entry?.offline ||
        (entry && Date.now() - entry.at > BACKGROUND_MS) ||
        offlineSyncStore.isInReplayWindow()
      if (isBackground) {
        offlineSyncStore.recordFailure(
          result.error.graphQLErrors[0]?.message ?? result.error.message,
        )
      }
      tracked.delete(operation.key)
    }
    return result
  },
})
